import React, { useEffect, useState, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { Truck, Warning, MapPin, VideoCamera, X, ArrowsHorizontal, Gauge, Wrench, ArrowCounterClockwise, MapTrifold, Broadcast } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import FELMachineIcon from "./FELMachineIcon";

/* FEL Operations Dashboard
 * - Dark theme management view
 * - KPI strip · Active Machines, Closest Distance, Today's Hours, Proximity Alerts
 * - Live map with machine markers, proximity rings, and distance lines drawn
 *   between every pair of active operators (live, updates every 3s)
 * - Side rail: Proximity Alert (closest pair), Recent Alerts, Safe-Distance guideline
 * - Bottom: Machine Working Hours (horizontal bars)
 * - Manager-only: Reset Proximity Counter button + Live camera feed buttons
 */

// Fix Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const RICHARDS_BAY = [-28.78, 32.05];

// ---- Marker (machine card on the map) ----
function makeMachineIcon({ label, speedKmh, stale, paused, alert }) {
    const bg = alert ? "#DC2626" : (stale ? "#475569" : (paused ? "#D97706" : "#0F766E"));
    const ring = alert ? "#FCA5A5" : (paused ? "#FDE68A" : "#A7F3D0");
    const html = `
      <div style="display:flex;flex-direction:column;align-items:center;gap:4px;pointer-events:none">
        <div style="
          background:${bg};color:white;
          font-family:Inter,sans-serif;font-weight:900;font-size:11px;
          padding:5px 9px;border-radius:8px;
          box-shadow:0 4px 14px rgba(0,0,0,0.6);
          border:2px solid ${ring};
          letter-spacing:0.5px;white-space:nowrap;
        ">${label}</div>
        <div style="
          background:rgba(15,23,42,0.92);color:#cbd5e1;
          font-family:Inter,sans-serif;font-size:10px;font-weight:600;
          padding:2px 6px;border-radius:4px;border:1px solid rgba(255,255,255,0.12);
        ">${speedKmh != null ? Number(speedKmh).toFixed(1) + " km/h" : "—"}</div>
      </div>`;
    return L.divIcon({ className: "fel-machine-marker", html, iconSize: [70, 50], iconAnchor: [35, 24] });
}

// ---- Distance label on the connecting line between two machines ----
function distanceLabelIcon(text, isAlert) {
    const bg = isAlert ? "#DC2626" : "#0EA5E9";
    return L.divIcon({
        className: "fel-dist-label",
        html: `<div style="background:${bg};color:white;font-family:Inter,sans-serif;font-weight:800;font-size:10px;padding:2px 6px;border-radius:9999px;box-shadow:0 2px 4px rgba(0,0,0,0.4);white-space:nowrap;">${text}</div>`,
        iconSize: [60, 18], iconAnchor: [30, 9],
    });
}

export default function FELLiveMap() {
    const { user } = useAuth();
    const canViewCamera = ["admin", "manager", "fleet_manager"].includes(user?.role);
    const canResetProx = ["admin", "manager", "fleet_manager"].includes(user?.role);
    const [sessions, setSessions] = useState([]);
    const [pairs, setPairs] = useState([]);
    const [threshold, setThreshold] = useState(10);
    const [basemap, setBasemap] = useState("satellite");   // "satellite" | "streets" — default to satellite so terrain is visible
    const [events, setEvents] = useState([]);
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [liveSession, setLiveSession] = useState(null);
    const [resetting, setResetting] = useState(false);
    const [viewMode, setViewMode] = useState("stationary"); // 'stationary' | 'map'
    const [showCameraPicker, setShowCameraPicker] = useState(false);

    const load = async () => {
        try {
            const [aRes, eRes, mRes] = await Promise.all([
                api.get("/fel/sessions/active"),
                api.get("/fel/proximity-events", { params: { limit: 10 } }),
                api.get("/fel/machines").catch(() => ({ data: [] })),
            ]);
            // Backwards compatible: older shape was an array
            if (Array.isArray(aRes.data)) {
                setSessions(aRes.data);
                setPairs([]);
            } else {
                setSessions(aRes.data?.sessions || []);
                setPairs(aRes.data?.pairs || []);
                setThreshold(aRes.data?.threshold_m || 10);
            }
            setEvents(eRes.data || []);
            setMachines(mRes.data || []);
        } catch { /* swallow */ }
        setLoading(false);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, []);

    const center = useMemo(() => {
        const withPos = sessions.filter((s) => s.last_lat != null && s.last_lng != null);
        if (withPos.length === 0) return RICHARDS_BAY;
        const lat = withPos.reduce((a, s) => a + s.last_lat, 0) / withPos.length;
        const lng = withPos.reduce((a, s) => a + s.last_lng, 0) / withPos.length;
        return [lat, lng];
    }, [sessions]);

    const liveCount = sessions.filter((s) => !s.stale).length;
    const totalAlerts = sessions.reduce((sum, s) => sum + (s.proximity_alerts || 0), 0);
    const totalHoursToday = sessions.reduce((sum, s) => {
        const started = s.started_at ? new Date(s.started_at).getTime() : null;
        if (!started) return sum;
        return sum + (Date.now() - started) / 3_600_000;
    }, 0);
    // Average current speed across all live machines (matches reference: "SPEED · Average")
    const avgSpeed = useMemo(() => {
        const live = sessions.filter((s) => !s.stale);
        if (!live.length) return 0;
        return live.reduce((sum, s) => sum + (s.last_speed_kmh || 0), 0) / live.length;
    }, [sessions]);
    const closestPair = pairs.length > 0
        ? pairs.slice().sort((a, b) => a.distance_m - b.distance_m)[0]
        : null;
    const closestDist = closestPair?.distance_m ?? null;
    const belowSafe = closestDist != null && closestDist < threshold;

    const sessionById = useMemo(() => {
        const m = {};
        for (const s of sessions) m[s.id] = s;
        return m;
    }, [sessions]);

    const resetProximity = async () => {
        if (!window.confirm("Reset the proximity-alert counter on every active operator and clear the recent-alerts log?")) return;
        setResetting(true);
        try {
            const r = await api.post("/fel/proximity-events/reset");
            toast.success(`Reset · ${r.data.sessions_reset} session(s), ${r.data.events_cleared} alert(s) cleared`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reset");
        } finally {
            setResetting(false);
        }
    };

    return (
        <div className="bg-slate-950 text-slate-100 rounded-md p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="fel-live-map">
            {/* Header */}
            <div className="flex items-center gap-3 px-1">
                <div className="w-9 h-9 rounded-md bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                    <Truck size={20} weight="fill" className="text-emerald-400" />
                </div>
                <div className="flex-1">
                    <div className="font-display font-black tracking-wide text-base sm:text-lg uppercase">FEL Operations Dashboard</div>
                    <div className="text-[11px] text-slate-400">Live · auto-updates every 3 s · Richards Bay Terminal</div>
                </div>
                {canResetProx && (
                    <button
                        onClick={resetProximity}
                        disabled={resetting}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-semibold text-slate-100 transition disabled:opacity-50"
                        data-testid="fel-reset-prox-btn"
                        title="Reset proximity-alert counter on all active sessions"
                    >
                        <ArrowCounterClockwise size={14} weight="bold" />
                        {resetting ? "Resetting…" : "Reset prox count"}
                    </button>
                )}
            </div>

            {/* KPI strip — Speed · Distance · Working hours · Machines in operation */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                <KpiCard Icon={Gauge} label="Speed" value={`${avgSpeed.toFixed(1)}`} unit="km/h"
                         sub="Average speed" accent="emerald" testid="kpi-speed" />
                <KpiCard Icon={ArrowsHorizontal} label="Distance to nearest machine"
                         value={closestDist != null ? `${closestDist.toFixed(1)}` : "—"} unit={closestDist != null ? "m" : ""}
                         sub={belowSafe ? "⚠ BELOW 10m SAFE DISTANCE" : (closestPair ? `${closestPair.a_machine_id} ↔ ${closestPair.b_machine_id}` : "all clear")}
                         accent={belowSafe ? "red" : (closestDist != null && closestDist < 20 ? "amber" : "sky")}
                         testid="kpi-closest-distance" />
                <KpiCard Icon={Gauge} label="Machine working hours"
                         value={totalHoursToday.toFixed(1)} unit="hrs" sub="Today"
                         accent="sky" testid="kpi-hours-today" />
                <KpiCard Icon={Truck} label="Machines in operation"
                         value={liveCount} sub="Active machines"
                         accent={totalAlerts > 0 ? "red" : "emerald"} testid="kpi-active-machines" />
            </div>

            {/* Main grid — map (8 cols) + side rail (4 cols) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
                <div className="lg:col-span-8 rounded-md overflow-hidden border border-slate-800" data-testid="fel-map-container">
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 border-b border-slate-800">
                        <MapPin size={14} weight="fill" className="text-emerald-400" />
                        <div className="font-display font-bold text-sm uppercase tracking-wide">Live Operation Map</div>
                        <span className="hidden sm:inline ml-auto text-[11px] text-slate-400">{liveCount} live · 3 s polling</span>
                    </div>
                    {/* View toggle */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-900/60 border-b border-slate-800 flex-wrap">
                        <button
                            onClick={() => setViewMode("stationary")}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                                viewMode === "stationary" ? "bg-emerald-500 text-slate-950" : "bg-slate-800 hover:bg-slate-700 text-slate-200"
                            }`}
                            data-testid="fel-view-stationary"
                        >
                            <Truck size={14} weight="fill" /> Stationary view
                        </button>
                        <button
                            onClick={() => setViewMode("map")}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                                viewMode === "map" ? "bg-sky-500 text-slate-950" : "bg-slate-800 hover:bg-slate-700 text-slate-200"
                            }`}
                            data-testid="fel-view-map"
                        >
                            <MapTrifold size={14} weight="fill" /> Live view map
                        </button>
                        {canViewCamera && (
                            <button
                                onClick={() => setShowCameraPicker(true)}
                                disabled={sessions.filter((s) => !s.stale).length === 0}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="fel-view-camera"
                            >
                                <Broadcast size={14} weight="fill" /> Live camera view
                            </button>
                        )}
                        <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${liveCount > 0 ? "bg-emerald-400 animate-pulse" : "bg-slate-600"}`} />
                            Live
                        </span>
                    </div>
                    {viewMode === "stationary" ? (
                        <StationaryView sessions={sessions} pairs={pairs} threshold={threshold} onView={canViewCamera ? setLiveSession : null} />
                    ) : (
                        <div style={{ height: 460 }} className="rounded-md overflow-hidden ring-1 ring-slate-300 shadow-inner relative">
                            {/* Basemap toggle — Streets vs Satellite so users can see actual terrain */}
                            <div className="absolute z-[500] top-2 right-2 flex bg-white rounded shadow border border-slate-300 overflow-hidden text-[10px] font-bold uppercase tracking-wider" data-testid="fel-basemap-toggle">
                                <button onClick={() => setBasemap("streets")}
                                    className={`px-2 py-1 ${basemap === "streets" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                                    data-testid="fel-basemap-streets">Streets</button>
                                <button onClick={() => setBasemap("satellite")}
                                    className={`px-2 py-1 ${basemap === "satellite" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                                    data-testid="fel-basemap-satellite">Satellite</button>
                            </div>
                            <MapContainer center={center} zoom={16} style={{ height: "100%", width: "100%", background: "#E2E8F0" }}>
                                <TileLayer
                                    key={basemap}
                                    url={basemap === "satellite"
                                        ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                        : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"}
                                    attribution={basemap === "satellite"
                                        ? '&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics'
                                        : '&copy; <a href="https://carto.com/">CARTO</a> · &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'}
                                    subdomains="abcd"
                                    maxZoom={20}
                                />
                                {/* Satellite-only label overlay so plant streets/labels are still legible */}
                                {basemap === "satellite" && (
                                    <TileLayer
                                        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                                        attribution=""
                                        subdomains="abcd"
                                        maxZoom={20}
                                        opacity={0.85}
                                    />
                                )}
                                <FitToMarkers sessions={sessions} />
                                {/* Distance lines between every pair of operators */}
                                {pairs.filter((p) => sessionById[p.a_session_id]?.last_lat != null && sessionById[p.b_session_id]?.last_lat != null).map((p) => {
                                    const a = sessionById[p.a_session_id];
                                    const b = sessionById[p.b_session_id];
                                    const mid = [(a.last_lat + b.last_lat) / 2, (a.last_lng + b.last_lng) / 2];
                                    return (
                                        <React.Fragment key={`${p.a_session_id}-${p.b_session_id}`}>
                                            <Polyline positions={[[a.last_lat, a.last_lng], [b.last_lat, b.last_lng]]}
                                                      pathOptions={{
                                                          color: p.below_safe ? "#DC2626" : "#0EA5E9",
                                                          opacity: 0.85,
                                                          weight: p.below_safe ? 3 : 2,
                                                          dashArray: p.below_safe ? null : "6 6",
                                                      }} />
                                            <Marker position={mid} icon={distanceLabelIcon(`${p.distance_m.toFixed(1)} m`, p.below_safe)} interactive={false} />
                                        </React.Fragment>
                                    );
                                })}
                                {/* Markers + proximity rings */}
                                {sessions.filter((s) => s.last_lat != null).map((s) => {
                                    const label = (s.machine_number || s.machine_id || "FEL").toString().slice(0, 8).toUpperCase();
                                    const alert = (s.proximity_alerts || 0) > 0 || (s.distance_to_nearest_m != null && s.distance_to_nearest_m < threshold);
                                    return (
                                        <React.Fragment key={s.id}>
                                            <Marker position={[s.last_lat, s.last_lng]}
                                                    icon={makeMachineIcon({ label, speedKmh: s.last_speed_kmh, stale: s.stale, paused: !!s.pause_state, alert })}>
                                                <Popup>
                                                    <div className="text-xs">
                                                        <div className="font-display font-black">{s.operator_name}</div>
                                                        <div>Machine: <b>{s.machine_number || s.machine_id}</b></div>
                                                        <div>Speed: {s.last_speed_kmh?.toFixed(1) ?? "0"} km/h · Max {s.max_speed_kmh?.toFixed(1) ?? "0"}</div>
                                                        <div>Op: {s.operation_type?.replace("_", " ")}</div>
                                                        {s.distance_to_nearest_m != null && (
                                                            <div>Nearest: <b>{s.nearest_machine_id}</b> @ <b>{s.distance_to_nearest_m.toFixed(1)} m</b></div>
                                                        )}
                                                        {s.pause_state && <div className="text-amber-700 font-semibold">PAUSED · {s.pause_state.reason}</div>}
                                                        {s.stale && <div className="text-slate-500">Stale (no recent ping)</div>}
                                                        {s.proximity_alerts > 0 && <div className="text-red-700 font-semibold">⚠ {s.proximity_alerts} proximity alert(s)</div>}
                                                        {canViewCamera && !s.stale && (
                                                            <button onClick={() => setLiveSession(s)} className="mt-2 btn-primary !py-1 !px-2 text-[10px] inline-flex items-center gap-1" data-testid={`fel-live-view-popup-${s.id}`}>
                                                                <VideoCamera size={10} weight="fill" /> View live camera
                                                            </button>
                                                        )}
                                                    </div>
                                                </Popup>
                                            </Marker>
                                            {!s.stale && (
                                                <Circle center={[s.last_lat, s.last_lng]} radius={threshold}
                                                        pathOptions={{ color: alert ? "#DC2626" : "#10B981", opacity: 0.5, weight: 1.5, fillOpacity: 0.08 }} />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </MapContainer>
                        </div>
                    )}
                </div>

                {/* Right rail */}
                <div className="lg:col-span-4 space-y-3 sm:space-y-4">
                    {/* Proximity Alert — closest pair */}
                    <div className="rounded-md border border-slate-800 overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 border-b border-slate-800">
                            <Warning size={14} weight="fill" className="text-red-400" />
                            <div className="font-display font-bold text-xs uppercase tracking-wide">Closest separation</div>
                        </div>
                        <div className="p-3" data-testid="fel-closest-pair">
                            {!closestPair ? (
                                <div className="text-center text-xs text-slate-500 py-5">No active pairs yet</div>
                            ) : (
                                <>
                                    <div className="flex items-baseline gap-2">
                                        <div className={`font-display font-black text-3xl tabular ${closestPair.below_safe ? "text-red-400" : "text-emerald-400"}`} data-testid="closest-distance-value">
                                            {closestPair.distance_m.toFixed(1)} m
                                        </div>
                                        {closestPair.below_safe && (
                                            <span className="text-[10px] uppercase font-bold text-red-300 bg-red-900/40 px-1.5 py-0.5 rounded">Below safe</span>
                                        )}
                                    </div>
                                    <div className="text-[12px] text-slate-300 mt-1">
                                        <b className="text-white">{closestPair.a_machine_id}</b> ↔ <b className="text-white">{closestPair.b_machine_id}</b>
                                    </div>
                                    <div className="text-[10.5px] text-slate-500 mt-2">Safe-distance threshold: {threshold} m</div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Recent Alerts */}
                    <div className="rounded-md border border-slate-800 overflow-hidden" data-testid="fel-prox-list">
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 border-b border-slate-800">
                            <Warning size={14} weight="fill" className="text-amber-400" />
                            <div className="font-display font-bold text-xs uppercase tracking-wide">Recent proximity alerts</div>
                            <span className="ml-auto text-[10px] text-slate-500">{events.length}</span>
                        </div>
                        {events.length === 0 ? (
                            <div className="text-center text-xs text-slate-500 py-6">No alerts logged</div>
                        ) : (
                            <ul className="divide-y divide-slate-800 text-[11px]">
                                {events.slice(0, 5).map((e) => (
                                    <li key={e.id} className="px-3 py-2 flex items-center gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-slate-200 truncate">
                                                <b>{e.machine_a}</b> ↔ <b>{e.machine_b}</b>
                                            </div>
                                            <div className="text-[10px] text-slate-500">{new Date(e.ts).toLocaleTimeString()}</div>
                                        </div>
                                        <span className="font-display font-black text-red-400 tabular">{e.distance_m} m</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Safe-distance guideline */}
                    <div className="rounded-md border border-emerald-700/40 bg-emerald-900/15 p-3">
                        <div className="flex items-center gap-2 mb-1">
                            <ArrowsHorizontal size={14} weight="fill" className="text-emerald-300" />
                            <div className="font-display font-bold text-xs uppercase tracking-wide text-emerald-200">Safe-distance guideline</div>
                        </div>
                        <div className="text-[11.5px] text-slate-200">
                            Maintain at least <b className="text-white">{threshold} m</b> between any two front-end loaders. Both operators are alerted (audible + screen flash) the moment they cross the threshold.
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom — Machine working hours */}
            <MachineHoursStrip machines={machines} sessions={sessions} />

            {/* Active session table — compact, dark variant */}
            <div className="rounded-md border border-slate-800 overflow-x-auto" data-testid="fel-session-list">
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 border-b border-slate-800 sticky top-0">
                    <Truck size={14} weight="fill" className="text-emerald-400" />
                    <div className="font-display font-bold text-xs uppercase tracking-wide">Active sessions</div>
                </div>
                {sessions.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500">No active FEL operators right now.</div>
                ) : (
                    <table className="w-full text-xs tabular">
                        <thead className="bg-slate-900/60 text-slate-400 uppercase text-[10px]">
                            <tr>
                                <th className="text-left p-2">Operator</th>
                                <th className="text-left p-2">Machine</th>
                                <th className="text-left p-2">Operation</th>
                                <th className="text-right p-2">Speed</th>
                                <th className="text-right p-2">Max</th>
                                <th className="text-right p-2">Avg</th>
                                <th className="text-right p-2">Nearest</th>
                                <th className="text-right p-2">Prox</th>
                                <th className="text-center p-2">Status</th>
                                {canViewCamera && <th className="text-center p-2">Camera</th>}
                            </tr>
                        </thead>
                        <tbody className="text-slate-200">
                            {sessions.map((s) => (
                                <tr key={s.id} className="border-t border-slate-800">
                                    <td className="p-2 font-semibold">{s.operator_name}</td>
                                    <td className="p-2">{s.machine_number || s.machine_id}</td>
                                    <td className="p-2 capitalize text-slate-400">{(s.operation_type || "").replace("_", " ")}</td>
                                    <td className="p-2 text-right">{(s.last_speed_kmh || 0).toFixed(1)}</td>
                                    <td className="p-2 text-right">{(s.max_speed_kmh || 0).toFixed(1)}</td>
                                    <td className="p-2 text-right">{(s.avg_speed_kmh || 0).toFixed(1)}</td>
                                    <td className={`p-2 text-right ${s.distance_to_nearest_m != null && s.distance_to_nearest_m < threshold ? "text-red-400 font-semibold" : ""}`}>
                                        {s.distance_to_nearest_m != null ? `${s.distance_to_nearest_m.toFixed(1)} m` : "—"}
                                    </td>
                                    <td className="p-2 text-right">{s.proximity_alerts || 0}</td>
                                    <td className="p-2 text-center">
                                        {s.pause_state ? <span className="text-amber-400 font-semibold uppercase text-[10px]">Paused</span>
                                         : s.stale ? <span className="text-slate-500 uppercase text-[10px]">Stale</span>
                                         : <span className="text-emerald-400 font-semibold uppercase text-[10px]">Live</span>}
                                    </td>
                                    {canViewCamera && (
                                        <td className="p-2 text-center">
                                            <button onClick={() => setLiveSession(s)} disabled={s.stale}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-500 disabled:opacity-30 disabled:cursor-not-allowed text-[10px] text-white font-semibold"
                                                    data-testid={`fel-live-view-row-${s.id}`}>
                                                <VideoCamera size={10} weight="fill" /> View
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {liveSession && (
                <LiveViewModal session={liveSession} onClose={() => setLiveSession(null)} />
            )}
            {showCameraPicker && (
                <CameraPickerModal
                    sessions={sessions.filter((s) => !s.stale)}
                    onPick={(s) => { setShowCameraPicker(false); setLiveSession(s); }}
                    onClose={() => setShowCameraPicker(false)}
                />
            )}
        </div>
    );
}

/* ---------------- Stationary illustration view ---------------- */
/* Renders each FEL machine as an SVG icon positioned by its GPS coordinates
 * (normalized to canvas %). Distance lines drawn between every pair, with
 * the distance label centered. Looks like a top-down operations diagram
 * over a stylised dark coal-pile background — independent of map tiles. */
function StationaryView({ sessions, pairs, threshold, onView }) {
    const withPos = sessions.filter((s) => s.last_lat != null && s.last_lng != null);

    // Compute bounds and normalize positions into 8-92% of canvas.
    const layout = useMemo(() => {
        if (withPos.length === 0) return { positions: {}, padded: false };
        if (withPos.length === 1) {
            const only = withPos[0];
            return { positions: { [only.id]: { x: 50, y: 50 } }, padded: false };
        }
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
        for (const s of withPos) {
            minLat = Math.min(minLat, s.last_lat); maxLat = Math.max(maxLat, s.last_lat);
            minLng = Math.min(minLng, s.last_lng); maxLng = Math.max(maxLng, s.last_lng);
        }
        const dLat = maxLat - minLat || 1e-6;
        const dLng = maxLng - minLng || 1e-6;
        const pad = 12; // % padding
        const positions = {};
        for (const s of withPos) {
            // Note: latitude increases northward → y inverted so north is up
            const x = pad + ((s.last_lng - minLng) / dLng) * (100 - 2 * pad);
            const y = pad + (1 - (s.last_lat - minLat) / dLat) * (100 - 2 * pad);
            positions[s.id] = { x, y };
        }
        return { positions, padded: true };
    }, [withPos]);

    const sessionMap = useMemo(() => Object.fromEntries(sessions.map((s) => [s.id, s])), [sessions]);

    return (
        <div
            className="relative overflow-hidden"
            style={{
                height: 460,
                background:
                    // Stylised dark stockpile — radial gradients form rocky mounds
                    "radial-gradient(circle at 30% 35%, rgba(71,85,105,0.45) 0%, transparent 35%)," +
                    "radial-gradient(circle at 70% 60%, rgba(51,65,85,0.6) 0%, transparent 40%)," +
                    "radial-gradient(circle at 50% 80%, rgba(30,41,59,0.7) 0%, transparent 35%)," +
                    "radial-gradient(ellipse at 20% 80%, rgba(15,23,42,0.6) 0%, transparent 50%)," +
                    "linear-gradient(180deg, #0B1220 0%, #020617 100%)",
            }}
            data-testid="fel-stationary-view"
        >
            {/* Subtle noise/texture overlay */}
            <svg
                className="absolute inset-0 w-full h-full opacity-20 pointer-events-none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <filter id="coal-noise">
                        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
                        <feColorMatrix values="0 0 0 0 0.6  0 0 0 0 0.6  0 0 0 0 0.6  0 0 0 0.35 0" />
                    </filter>
                </defs>
                <rect width="100%" height="100%" filter="url(#coal-noise)" />
            </svg>

            {withPos.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">
                    No active FEL machines yet — operators with GPS will appear here.
                </div>
            ) : (
                <>
                    {/* SVG layer for distance lines between every pair */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {pairs.map((p) => {
                            const a = layout.positions[p.a_session_id];
                            const b = layout.positions[p.b_session_id];
                            if (!a || !b) return null;
                            const stroke = p.below_safe ? "#DC2626" : "#10B981";
                            return (
                                <line
                                    key={`${p.a_session_id}-${p.b_session_id}`}
                                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                                    stroke={stroke}
                                    strokeWidth={p.below_safe ? 0.45 : 0.3}
                                    strokeDasharray={p.below_safe ? "1.2 0.8" : "0.6 0.6"}
                                    opacity="0.85"
                                    vectorEffect="non-scaling-stroke"
                                />
                            );
                        })}
                    </svg>

                    {/* Distance labels (positioned in % so they scale with container) */}
                    {pairs.map((p) => {
                        const a = layout.positions[p.a_session_id];
                        const b = layout.positions[p.b_session_id];
                        if (!a || !b) return null;
                        const mx = (a.x + b.x) / 2;
                        const my = (a.y + b.y) / 2;
                        return (
                            <div
                                key={`label-${p.a_session_id}-${p.b_session_id}`}
                                className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                                style={{ left: `${mx}%`, top: `${my}%` }}
                            >
                                <div className={`text-[11px] font-display font-black tabular px-2 py-0.5 rounded-md ${
                                    p.below_safe ? "bg-red-600 text-white shadow-lg" : "bg-slate-800/90 text-emerald-300 border border-emerald-500/40"
                                }`}>
                                    {p.distance_m.toFixed(1)} m
                                </div>
                            </div>
                        );
                    })}

                    {/* FEL machines */}
                    {withPos.map((s) => {
                        const pos = layout.positions[s.id];
                        if (!pos) return null;
                        const alert = (s.proximity_alerts || 0) > 0 || (s.distance_to_nearest_m != null && s.distance_to_nearest_m < threshold);
                        const ringColor = alert ? "rgba(220, 38, 38, 0.55)" : (s.pause_state ? "rgba(245, 158, 11, 0.55)" : "rgba(16, 185, 129, 0.55)");
                        const label = (s.machine_number || s.machine_id || "FEL").toString().toUpperCase();
                        return (
                            <div
                                key={s.id}
                                className="absolute -translate-x-1/2 -translate-y-1/2 group"
                                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                                data-testid={`stationary-machine-${s.machine_id || s.id}`}
                            >
                                {/* Proximity rings — three concentric rings, pulsing */}
                                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                                    {[64, 48, 32].map((sz, idx) => (
                                        <div
                                            key={sz}
                                            className="absolute rounded-full"
                                            style={{
                                                width: sz, height: sz,
                                                left: -sz / 2, top: -sz / 2,
                                                border: `1.5px solid ${ringColor}`,
                                                opacity: 0.5 - idx * 0.1,
                                                animation: alert ? "felPulseAlert 1s ease-in-out infinite" : undefined,
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* FEL machine illustration */}
                                <div className="relative">
                                    <FELMachineIcon size={64} alert={alert} paused={!!s.pause_state} stale={!!s.stale} />
                                </div>

                                {/* Label box */}
                                <div className="absolute left-1/2 -translate-x-1/2 -top-9 whitespace-nowrap" style={{ minWidth: 64 }}>
                                    <div className="px-1.5 py-0.5 rounded bg-slate-900/95 border border-slate-700 text-center shadow-md">
                                        <div className="font-display font-black text-[11px] text-white leading-tight">{label}</div>
                                        <div className={`text-[10px] font-semibold tabular leading-tight ${alert ? "text-red-300" : "text-emerald-300"}`}>
                                            {(s.last_speed_kmh || 0).toFixed(1)} km/h
                                        </div>
                                    </div>
                                </div>

                                {onView && !s.stale && (
                                    <button
                                        onClick={() => onView(s)}
                                        className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition px-2 py-0.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[10px] inline-flex items-center gap-1 shadow"
                                        data-testid={`stationary-camera-${s.id}`}
                                    >
                                        <VideoCamera size={10} weight="fill" /> Camera
                                    </button>
                                )}
                            </div>
                        );
                    })}

                    {/* Scale bar (informational — based on actual bounds) */}
                    <ScaleBar pairs={pairs} layout={layout} />
                </>
            )}

            {/* keyframes for alert ring pulse */}
            <style>{`
                @keyframes felPulseAlert {
                    0% { transform: scale(0.95); opacity: 0.8; }
                    50% { transform: scale(1.1); opacity: 0.3; }
                    100% { transform: scale(0.95); opacity: 0.8; }
                }
            `}</style>
        </div>
    );
}

/* Approximate scale bar — find a representative pair distance and draw a
   bar showing roughly how long X metres looks on the current canvas. */
function ScaleBar({ pairs, layout }) {
    // Use the median pair as the reference; default 20m
    if (!pairs.length) {
        return (
            <div className="absolute bottom-3 right-3 flex flex-col items-end text-slate-300">
                <div className="h-px bg-slate-300 w-16 mb-1"></div>
                <div className="text-[10px] uppercase tracking-wider">20 m</div>
            </div>
        );
    }
    const sorted = pairs.slice().sort((a, b) => a.distance_m - b.distance_m);
    const ref = sorted[Math.floor(sorted.length / 2)];
    const aPos = layout.positions[ref.a_session_id];
    const bPos = layout.positions[ref.b_session_id];
    if (!aPos || !bPos) return null;
    const dx = Math.abs(aPos.x - bPos.x);
    const dy = Math.abs(aPos.y - bPos.y);
    const pctDist = Math.sqrt(dx * dx + dy * dy); // % of canvas diagonal
    // 20 m is target — scale bar width proportional
    const targetMeters = ref.distance_m < 5 ? 5 : ref.distance_m < 15 ? 10 : 20;
    const widthPct = (targetMeters / ref.distance_m) * pctDist;
    return (
        <div className="absolute bottom-3 right-3 flex flex-col items-end text-slate-300">
            <div className="h-px bg-slate-300 mb-1" style={{ width: `${Math.max(8, Math.min(35, widthPct))}%` }} />
            <div className="text-[10px] uppercase tracking-wider">{targetMeters} m</div>
        </div>
    );
}

/* ---------------- Camera picker modal — pick which operator to view ---------------- */
function CameraPickerModal({ sessions, onPick, onClose }) {
    // Auto-pick if only one
    useEffect(() => {
        if (sessions.length === 1) onPick(sessions[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (sessions.length <= 1) return null;
    return (
        <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="fel-camera-picker">
            <div className="bg-slate-900 border border-slate-700 text-white rounded-md w-full max-w-md p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-3">
                    <Broadcast size={16} weight="fill" className="text-red-400" />
                    <div className="font-display font-black uppercase tracking-wide">Pick operator to view</div>
                    <button onClick={onClose} className="ml-auto text-slate-400 hover:text-white"><X size={16} /></button>
                </div>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {sessions.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => onPick(s)}
                            className="w-full text-left p-3 rounded-md bg-slate-800 hover:bg-slate-700 transition flex items-center gap-3"
                            data-testid={`camera-pick-${s.id}`}
                        >
                            <FELMachineIcon size={48} />
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-sm truncate">{s.operator_name}</div>
                                <div className="text-[11px] text-slate-400">{s.machine_number || s.machine_id} · {(s.last_speed_kmh || 0).toFixed(1)} km/h</div>
                            </div>
                            <VideoCamera size={16} weight="fill" className="text-red-400" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Bottom strip — machine working hours ---------------- */
function MachineHoursStrip({ machines, sessions }) {
    // Aggregate: lifetime current_hours per machine + today's session minutes.
    const todayMinByMachine = useMemo(() => {
        const map = {};
        for (const s of sessions) {
            const key = s.machine_id;
            if (!key) continue;
            const started = s.started_at ? new Date(s.started_at).getTime() : null;
            if (!started) continue;
            const ended = s.ended_at ? new Date(s.ended_at).getTime() : Date.now();
            map[key] = (map[key] || 0) + (ended - started) / 60000;
        }
        return map;
    }, [sessions]);
    const items = (machines || []).slice().sort((a, b) => (b.current_hours || 0) - (a.current_hours || 0)).slice(0, 8);
    const maxHours = items.reduce((m, x) => Math.max(m, x.service_hours || 250), 250);
    if (items.length === 0) return null;
    return (
        <div className="rounded-md border border-slate-800 overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 border-b border-slate-800">
                <Wrench size={14} weight="fill" className="text-amber-400" />
                <div className="font-display font-bold text-xs uppercase tracking-wide">Machine working hours · service interval</div>
            </div>
            <div className="p-3 space-y-1.5" data-testid="machine-hours-strip">
                {items.map((m) => {
                    const cur = Number(m.current_hours || 0);
                    const due = Number(m.service_hours || 250);
                    const pct = Math.min(100, (cur / due) * 100);
                    const overdue = cur >= due;
                    const near = !overdue && cur >= due - 36;
                    const todayMin = todayMinByMachine[m.machine_id];
                    return (
                        <div key={m.machine_id} className="grid grid-cols-12 items-center gap-2 text-[11.5px]">
                            <div className="col-span-3 sm:col-span-2 font-semibold text-slate-200 truncate" title={m.label}>
                                {m.label || m.machine_id}
                            </div>
                            <div className="col-span-6 sm:col-span-8">
                                <div className="h-2 rounded bg-slate-800 overflow-hidden relative">
                                    <div className="h-full transition-all"
                                         style={{
                                             width: `${pct}%`,
                                             background: overdue ? "#DC2626" : (near ? "#F59E0B" : "#10B981"),
                                         }} />
                                </div>
                            </div>
                            <div className={`col-span-3 sm:col-span-2 text-right tabular ${overdue ? "text-red-400 font-bold" : near ? "text-amber-400 font-semibold" : "text-slate-300"}`}>
                                {cur.toFixed(1)} / {due} h
                                {todayMin ? <span className="text-[10px] text-slate-500 ml-1">+{(todayMin / 60).toFixed(1)}h today</span> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ---------------- Live camera viewer (manager-side) ---------------- */
function LiveViewModal({ session, onClose }) {
    const [frame, setFrame] = useState(null);
    const [ts, setTs] = useState(null);
    const [status, setStatus] = useState("requesting"); // requesting | waiting | streaming | error
    const [fps, setFps] = useState(0);
    const esRef = useRef(null);
    const pollTimerRef = useRef(null);
    const stoppedRef = useRef(false);
    const frameCountRef = useRef(0);
    const fpsTimerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const recvFrame = (data) => {
            if (cancelled || !data?.frame) return;
            setFrame(data.frame);
            setTs(data.ts);
            setStatus("streaming");
            frameCountRef.current += 1;
        };
        (async () => {
            try {
                await api.post(`/fel/sessions/${session.id}/live-view/request`);
                if (cancelled) return;
                setStatus("waiting");

                // Prefer Server-Sent Events for live push at 30 fps. Cookies
                // are sent automatically because the SSE URL is same-origin
                // when accessed through REACT_APP_BACKEND_URL.
                const backend = process.env.REACT_APP_BACKEND_URL;
                const streamUrl = `${backend}/api/fel/sessions/${session.id}/live-view/stream`;
                let sseFailed = false;
                try {
                    const es = new EventSource(streamUrl, { withCredentials: true });
                    esRef.current = es;
                    es.onmessage = (evt) => {
                        try { recvFrame(JSON.parse(evt.data)); } catch { /* ignore */ }
                    };
                    es.onerror = () => {
                        // If SSE never opened, mark failed so we fall back to polling
                        if (es.readyState === EventSource.CLOSED) {
                            sseFailed = true;
                            es.close();
                            esRef.current = null;
                        }
                    };
                } catch {
                    sseFailed = true;
                }

                // Polling fallback — fires only if SSE doesn't yield a frame
                // within 2 s, and runs at ~30 fps (33 ms). On modern browsers
                // this still benefits from HTTP keep-alive.
                setTimeout(() => {
                    if (cancelled) return;
                    if (frameCountRef.current === 0 || sseFailed) {
                        const poll = async () => {
                            if (cancelled) return;
                            try {
                                const r = await api.get(`/fel/sessions/${session.id}/live-view/frame`);
                                recvFrame(r.data);
                            } catch { /* swallow */ }
                        };
                        poll();
                        pollTimerRef.current = setInterval(poll, 33);
                    }
                }, 2000);

                // FPS meter — count frames per second
                fpsTimerRef.current = setInterval(() => {
                    setFps(frameCountRef.current);
                    frameCountRef.current = 0;
                }, 1000);
            } catch (e) {
                if (cancelled) return;
                setStatus("error");
                toast.error("Could not request live view");
            }
        })();
        return () => {
            cancelled = true;
            if (esRef.current) { esRef.current.close(); esRef.current = null; }
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            if (fpsTimerRef.current) clearInterval(fpsTimerRef.current);
            if (!stoppedRef.current) {
                stoppedRef.current = true;
                api.post(`/fel/sessions/${session.id}/live-view/stop`).catch(() => {});
            }
        };
    }, [session.id]);

    return (
        <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="fel-live-view-modal">
            <div className="bg-white w-full max-w-2xl rounded-sm overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark">
                    <VideoCamera size={16} weight="fill" className="text-red-400" />
                    <div className="font-display font-bold uppercase tracking-wide">Live camera · {session.operator_name} · {session.machine_number || session.machine_id}</div>
                    <button onClick={onClose} className="ml-auto text-white" data-testid="fel-live-view-close"><X size={16} /></button>
                </div>
                <div className="bg-black aspect-video flex items-center justify-center relative" data-testid="fel-live-view-frame">
                    {frame ? (
                        <img src={`data:image/jpeg;base64,${frame}`} alt="live camera" className="max-h-full max-w-full" />
                    ) : (
                        <div className="text-center text-white/70 text-sm">
                            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse mx-auto mb-2" />
                            {status === "requesting" && "Requesting camera from operator…"}
                            {status === "waiting" && "Waiting for operator to grant camera permission…"}
                            {status === "error" && "Failed to start live view"}
                        </div>
                    )}
                    {status === "streaming" && (
                        <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                        </div>
                    )}
                </div>
                <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center gap-2 border-t border-slate-100">
                    <span>Live stream · <b className="text-emerald-600 tabular">{fps} fps</b> · Operation: <b className="capitalize">{(session.operation_type || "").replace("_", " ")}</b></span>
                    <span className="ml-auto">{ts ? `Last frame: ${new Date(ts).toLocaleTimeString()}` : "—"}</span>
                </div>
            </div>
        </div>
    );
}

function FitToMarkers({ sessions }) {
    const map = useMap();
    useEffect(() => {
        const pts = sessions.filter((s) => s.last_lat != null).map((s) => [s.last_lat, s.last_lng]);
        if (pts.length === 0) return;
        if (pts.length === 1) {
            map.setView(pts[0], Math.max(map.getZoom(), 17));
        } else {
            map.fitBounds(pts, { padding: [40, 40], maxZoom: 18 });
        }
    }, [sessions, map]);
    return null;
}

const ACCENT_BG = {
    emerald: "bg-emerald-500/15 border-emerald-400/30 text-emerald-300",
    sky: "bg-sky-500/15 border-sky-400/30 text-sky-300",
    amber: "bg-amber-500/15 border-amber-400/30 text-amber-300",
    red: "bg-red-500/15 border-red-400/30 text-red-300",
    slate: "bg-slate-500/15 border-slate-400/30 text-slate-300",
};

function KpiCard({ Icon, label, value, unit, sub, accent = "emerald", testid }) {
    const accentClass = ACCENT_BG[accent] || ACCENT_BG.emerald;
    const valueColor =
        accent === "red" ? "text-red-400"
            : accent === "amber" ? "text-amber-300"
                : accent === "sky" ? "text-sky-300"
                    : "text-emerald-300";
    return (
        <div className="rounded-md border border-slate-800 bg-slate-900/60 p-3 flex items-start gap-3" data-testid={testid}>
            <div className={`w-9 h-9 rounded-md flex items-center justify-center border ${accentClass}`}>
                <Icon size={18} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
                <div className="flex items-baseline gap-1.5">
                    <div className={`font-display font-black text-2xl sm:text-3xl tabular ${valueColor}`} data-testid={`${testid}-value`}>{value}</div>
                    {unit && <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{unit}</div>}
                </div>
                {sub && <div className={`text-[10.5px] truncate ${accent === "red" ? "text-red-400 font-bold" : "text-slate-400"}`}>{sub}</div>}
            </div>
        </div>
    );
}
